import React, { useMemo } from 'react';
import classes from './MarkdownRenderer.module.css';

interface IProps {
  text: string;
  /** If true, only the last top-level # heading section is shown (filters Deep Research thinking steps) */
  extractFinalReport?: boolean;
}

/** Strip Deep-Research internal noise from the raw response text */
function preprocess(text: string, extractFinal: boolean): string {
  // 1. Extract only the LAST top-level "# ..." section when requested
  if (extractFinal) {
    const h1Re = /^# .+/gm;
    let lastIndex = -1;
    let m: RegExpExecArray | null;
    while ((m = h1Re.exec(text)) !== null) {
      lastIndex = m.index;
    }
    if (lastIndex > 0) {
      text = text.slice(lastIndex);
    }
  }

  // 2. Remove ```json ... ``` blocks (agent thinking steps)
  text = text.replace(/```json[\s\S]*?```/g, '');

  // 3. Remove <visual_element>...</visual_element> blocks
  text = text.replace(/<visual_element>[\s\S]*?<\/visual_element>/g, '');

  // 4. Remove meta-directives sections that start with "**1. Required Directives**" etc.
  text = text.replace(/\*\*\d+\. Required Directives\*\*[\s\S]*/g, '');
  text = text.replace(/\*\*\d+\. The Next Level Directives\*\*[\s\S]*/g, '');

  // 5. Collapse multiple blank lines
  text = text.replace(/\n{3,}/g, '\n\n');

  return text.trim();
}

/** Inline markdown: bold, italic, inline code, links */
function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  // Patterns: **bold**, *italic*, `code`, [text](url)
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+?)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(text.slice(last, m.index));
    }
    if (m[2]) {
      parts.push(<strong key={i++}>{m[2]}</strong>);
    } else if (m[3]) {
      parts.push(<em key={i++}>{m[3]}</em>);
    } else if (m[4]) {
      parts.push(<code key={i++} className={classes.inlineCode}>{m[4]}</code>);
    } else if (m[5] && m[6]) {
      parts.push(<a key={i++} href={m[6]} target="_blank" rel="noopener noreferrer" className={classes.link}>{m[5]}</a>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>;
}

type Block =
  | { type: 'h1' | 'h2' | 'h3' | 'h4'; text: string }
  | { type: 'blockquote'; text: string }
  | { type: 'hr' }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] }
  | { type: 'table'; header: string[]; rows: string[][] }
  | { type: 'p'; text: string }
  | { type: 'empty' };

function parseBlocks(text: string): Block[] {
  const lines = text.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { blocks.push({ type: 'empty' }); i++; continue; }

    // Headings
    const h1 = trimmed.match(/^# (.+)/);
    if (h1) { blocks.push({ type: 'h1', text: h1[1] }); i++; continue; }
    const h2 = trimmed.match(/^## (.+)/);
    if (h2) { blocks.push({ type: 'h2', text: h2[1] }); i++; continue; }
    const h3 = trimmed.match(/^### (.+)/);
    if (h3) { blocks.push({ type: 'h3', text: h3[1] }); i++; continue; }
    const h4 = trimmed.match(/^#### (.+)/);
    if (h4) { blocks.push({ type: 'h4', text: h4[1] }); i++; continue; }

    // HR
    if (/^---+$/.test(trimmed)) { blocks.push({ type: 'hr' }); i++; continue; }

    // Blockquote
    if (trimmed.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        quoteLines.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'blockquote', text: quoteLines.join(' ') });
      continue;
    }

    // Table — detect by | ... | pattern
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        const header = tableLines[0].split('|').slice(1, -1).map(c => c.trim());
        const dataRows = tableLines.slice(2).map(row =>
          row.split('|').slice(1, -1).map(c => c.trim())
        );
        blocks.push({ type: 'table', header, rows: dataRows });
      }
      continue;
    }

    // Unordered list
    if (/^[*-] /.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[*-] /.test(lines[i])) {
        items.push(lines[i].trim().replace(/^[*-] /, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Ordered list
    if (/^\d+\. /.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\. /.test(lines[i])) {
        items.push(lines[i].trim().replace(/^\d+\. /, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Paragraph — collect until blank line or heading
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().match(/^#{1,4} /) &&
      !lines[i].trim().match(/^[*-] /) &&
      !lines[i].trim().match(/^\d+\. /) &&
      !lines[i].trim().startsWith('|') &&
      !lines[i].trim().startsWith('>') &&
      !/^---+$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: 'p', text: paraLines.join(' ') });
    }
  }
  return blocks;
}

const MarkdownRenderer: React.FC<IProps> = ({ text, extractFinalReport = false }) => {
  const blocks = useMemo(() => {
    const cleaned = preprocess(text, extractFinalReport);
    return parseBlocks(cleaned);
  }, [text, extractFinalReport]);

  return (
    <div className={classes.root}>
      {blocks.map((block, idx) => {
        switch (block.type) {
          case 'h1': return <h1 key={idx} className={classes.h1}>{renderInline(block.text)}</h1>;
          case 'h2': return <h2 key={idx} className={classes.h2}>{renderInline(block.text)}</h2>;
          case 'h3': return <h3 key={idx} className={classes.h3}>{renderInline(block.text)}</h3>;
          case 'h4': return <h4 key={idx} className={classes.h4}>{renderInline(block.text)}</h4>;
          case 'hr': return <hr key={idx} className={classes.hr} />;
          case 'blockquote': return (
            <blockquote key={idx} className={classes.blockquote}>
              {renderInline(block.text)}
            </blockquote>
          );
          case 'ul': return (
            <ul key={idx} className={classes.ul}>
              {block.items.map((item, j) => (
                <li key={j} className={classes.li}>{renderInline(item)}</li>
              ))}
            </ul>
          );
          case 'ol': return (
            <ol key={idx} className={classes.ol}>
              {block.items.map((item, j) => (
                <li key={j} className={classes.li}>{renderInline(item)}</li>
              ))}
            </ol>
          );
          case 'table': return (
            <div key={idx} className={classes.tableWrapper}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    {block.header.map((h, j) => (
                      <th key={j} className={classes.th}>{renderInline(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? classes.trEven : classes.trOdd}>
                      {row.map((cell, ci) => (
                        <td key={ci} className={classes.td}>{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          case 'p': return <p key={idx} className={classes.p}>{renderInline(block.text)}</p>;
          case 'empty': return null;
          default: return null;
        }
      })}
    </div>
  );
};

export default MarkdownRenderer;
